import { DeleteOutlined, InboxOutlined, LinkOutlined, PlusOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { PaidOnlyTag, SharedSpinner, SupplierSelect } from '@components';
import { useView } from '@hooks';
import { L } from '@lib';
import { SupplierLookup } from '@services';
import { Button, Col, DatePicker, Drawer, Form, Input, Modal, Row, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { InvoiceFileUpload } from '../InvoiceFileUpload';
import { ParsedItemCard } from '../ParsedItemCard';

const { Text } = Typography;

interface IProps {
  open: boolean;
  uploadedFile: { base64: string; name: string; contentType: string } | null;
  parsedItems: any[];
  parsing: boolean;
  captureToken: string | null;
  parsedSupplierName: string;
  parsedInvoiceNumber: string;
  bulkSupplierId: string | undefined;
  attachedSupplier: SupplierLookup | undefined;
  categoryOptions: { label: string; value: string }[];
  serviceOptions: { id: number; name: string }[];
  submitting: boolean;
  supplierModalOpen: boolean;
  supplierCreating: boolean;
  onOpenSupplierModal: () => void;
  onCloseSupplierModal: () => void;
  onCreateSupplier: (values: {
    name: string;
    contactName?: string;
    email?: string;
    phone?: string;
    address?: string;
  }) => void;
  onClose: () => void;
  onAttachSupplier: () => void;
  onSubmit: () => void;
  onFileSelect: (file: File) => void;
  onRemoveUploadedFile: () => void;
  onParse: () => void;
  onParsedItemChange: (index: number, field: string, value: any) => void;
  onRemoveParsedItem: (index: number) => void;
  onAddParsedItem: () => void;
  onBulkSupplierChange: (value: string | undefined) => void;
  onParsedInvoiceNumberChange: (value: string) => void;
  bulkPurchaseDate?: string;
  onBulkPurchaseDateChange: (value: string | undefined) => void;
}

const InventoryUploadDrawerPresentation: React.FC<IProps> = ({
  open,
  uploadedFile,
  parsedItems,
  parsing,
  captureToken,
  parsedSupplierName,
  parsedInvoiceNumber,
  bulkSupplierId,
  attachedSupplier,
  categoryOptions,
  serviceOptions,
  submitting,
  supplierModalOpen,
  supplierCreating,
  onOpenSupplierModal,
  onCloseSupplierModal,
  onCreateSupplier,
  onClose,
  onAttachSupplier,
  onSubmit,
  onFileSelect,
  onRemoveUploadedFile,
  onParse,
  onParsedItemChange,
  onRemoveParsedItem,
  onAddParsedItem,
  onBulkSupplierChange,
  onParsedInvoiceNumberChange,
  bulkPurchaseDate,
  onBulkPurchaseDateChange,
}) => {
  const [supplierForm] = Form.useForm();
  const { isMobile } = useView();

  const handleSupplierModalClose = () => {
    supplierForm.resetFields();
    onCloseSupplierModal();
  };

  const handleSupplierModalSave = async () => {
    const values = await supplierForm.validateFields();
    onCreateSupplier(values);
    supplierForm.resetFields();
  };

  const isImage = !!uploadedFile && uploadedFile.contentType.startsWith('image/');

  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    setZoom(1);
  }, [uploadedFile?.base64]);
  const zoomOut = () => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)));
  const zoomIn = () => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)));

  const scrollRef = useRef<HTMLDivElement>(null);
  const prevZoomRef = useRef(1);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && zoom !== prevZoomRef.current && zoom > 1) {
      const ratio = zoom / Math.max(prevZoomRef.current, 1);
      el.scrollLeft = (el.scrollLeft + el.clientWidth / 2) * ratio - el.clientWidth / 2;
      el.scrollTop = (el.scrollTop + el.clientHeight / 2) * ratio - el.clientHeight / 2;
    }
    prevZoomRef.current = zoom;
  }, [zoom]);

  const dragRef = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const onPanStart = (e: React.MouseEvent) => {
    if (zoom === 1 || !scrollRef.current) return;
    e.preventDefault();
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      left: scrollRef.current.scrollLeft,
      top: scrollRef.current.scrollTop,
    };
    setIsDragging(true);
  };

  const onPanMove = (e: React.MouseEvent) => {
    if (!dragRef.current || !scrollRef.current) return;
    scrollRef.current.scrollLeft = dragRef.current.left - (e.clientX - dragRef.current.x);
    scrollRef.current.scrollTop = dragRef.current.top - (e.clientY - dragRef.current.y);
  };

  const onPanEnd = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  const parsingIndicator = parsing && (
    <div style={{ padding: 24, textAlign: 'center' }}>
      <SharedSpinner loading />
      <div style={{ marginTop: 12, color: '#8c8c8c', fontSize: 13 }}>
        {L('Extracting data from invoice...')}
      </div>
    </div>
  );

  const sharedAndItems = (
    <div style={{ marginTop: uploadedFile ? 0 : 16 }}>
      {bulkSupplierId && parsedItems.some((i) => i.reference || i.partNumber) && (
        <div
          style={{
            padding: '8px 12px',
            marginBottom: 12,
            background: '#e6f4ff',
            border: '1px solid #91caff',
            borderRadius: 6,
            fontSize: 12,
            color: '#0958d9',
          }}
        >
          {L('Items with the same reference and supplier as existing stock will merge — quantities add up, cost price updates to the new invoice, existing sell price is preserved.')}
        </div>
      )}

      <div style={{ padding: 12, background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0', marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
          {L('Shared details (applied to all items)')}
        </Text>
        {parsedSupplierName && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {L('Detected supplier:')} <strong>{parsedSupplierName}</strong>
            </Text>
            {attachedSupplier && attachedSupplier.id === bulkSupplierId ? (
              <Text style={{ fontSize: 12, color: '#52c41a' }}>✓ {L('Attached')}</Text>
            ) : (
              <Button
                size="small"
                type="link"
                icon={<LinkOutlined />}
                onClick={onAttachSupplier}
                style={{ padding: 0, height: 'auto' }}
              >
                {L('Attach supplier')}
              </Button>
            )}
          </div>
        )}
        <Row gutter={8}>
          <Col span={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Text style={{ fontSize: 12, color: '#8c8c8c' }}>
                {L('Supplier')}<span style={{ color: '#ff4d4f', marginLeft: 2 }}>*</span>
              </Text>
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={onOpenSupplierModal}
                style={{ padding: 0, height: 'auto', fontSize: 12 }}
              >
                {L('New')}
              </Button>
            </div>
            <SupplierSelect
              value={bulkSupplierId}
              onChange={onBulkSupplierChange}
              preselected={attachedSupplier}
            />
          </Col>
          <Col span={12}>
            <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>
              {L('Invoice Number')}<span style={{ color: '#ff4d4f', marginLeft: 2 }}>*</span>
            </Text>
            <Input
              value={parsedInvoiceNumber}
              onChange={(e) => onParsedInvoiceNumberChange(e.target.value)}
              placeholder={L('Invoice number')}
              status={!parsedInvoiceNumber.trim() ? 'error' : undefined}
            />
          </Col>
        </Row>
        <Row gutter={8} style={{ marginTop: 8 }}>
          <Col span={12}>
            <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>
              {L('Purchase date (applied to all items)')}
            </Text>
            <DatePicker
              style={{ width: '100%' }}
              value={bulkPurchaseDate ? dayjs(bulkPurchaseDate) : undefined}
              onChange={(d) => onBulkPurchaseDateChange(d ? d.toISOString() : undefined)}
              placeholder={L('Pick invoice date')}
            />
          </Col>
        </Row>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>{L('Items')}</span>
        <Button
          icon={<PlusOutlined />}
          onClick={onAddParsedItem}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
        >
          {L('Add Item')}
        </Button>
      </div>

      {serviceOptions.length > 0 && (
        <div
          style={{
            padding: '8px 12px',
            marginBottom: 12,
            background: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: 6,
            fontSize: 12,
            color: '#389e0d',
          }}
        >
          {L('Every item is saved to inventory. Assigning a service additionally adds the item as a part on that service.')}
        </div>
      )}

      {parsedItems.map((item, index) => (
        <ParsedItemCard
          key={index}
          index={index}
          item={item}
          categoryOptions={categoryOptions}
          serviceOptions={serviceOptions}
          onChange={onParsedItemChange}
          onRemove={onRemoveParsedItem}
        />
      ))}
    </div>
  );

  const invoicePanel = uploadedFile && (
    <div
      style={{
        ...(isMobile
          ? { width: '100%', maxHeight: '40vh', borderBottom: '1px solid #e8e8e8', flexShrink: 0 }
          : { width: 900, flexShrink: 0, borderLeft: '1px solid #e8e8e8' }),
        display: 'flex',
        flexDirection: 'column',
        background: '#fafafa',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 14px',
          borderBottom: '1px solid #e8e8e8',
          background: '#fff',
          gap: 8,
        }}
      >
        <InboxOutlined style={{ fontSize: 16, color: '#2CBDFF', flexShrink: 0 }} />
        <Text strong style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>
          {uploadedFile.name}
        </Text>
        {isImage && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            <Button type="text" size="small" icon={<ZoomOutOutlined />} disabled={zoom <= 1} onClick={zoomOut} />
            <span style={{ fontSize: 12, color: '#8c8c8c', minWidth: 36, textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </span>
            <Button type="text" size="small" icon={<ZoomInOutlined />} disabled={zoom >= 4} onClick={zoomIn} />
          </div>
        )}
        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={onRemoveUploadedFile}>
          {L('Remove')}
        </Button>
      </div>

      <div
        ref={scrollRef}
        onMouseDown={onPanStart}
        onMouseMove={isDragging ? onPanMove : undefined}
        onMouseUp={onPanEnd}
        onMouseLeave={onPanEnd}
        style={{
          flex: 1,
          overflow: 'auto',
          padding: 16,
          minHeight: 0,
          background: '#fff',
          userSelect: isDragging ? 'none' : 'auto',
          cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
          ...(zoom === 1
            ? { display: 'flex', alignItems: 'center', justifyContent: 'center' }
            : {}),
        }}
      >
        {isImage ? (
          <img
            src={uploadedFile.base64}
            alt={uploadedFile.name}
            draggable={false}
            style={
              zoom === 1
                ? { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }
                : { width: `${zoom * 100}%`, maxWidth: 'none', height: 'auto', display: 'block', pointerEvents: 'none' }
            }
          />
        ) : (
          <iframe
            src={uploadedFile.base64}
            title={uploadedFile.name}
            style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
          />
        )}
      </div>

      <div style={{ padding: '12px 14px', borderTop: '1px solid #e8e8e8', background: '#fff', display: 'flex', justifyContent: 'center' }}>
        <Button
          onClick={onParse}
          type="primary"
          loading={parsing}
        >
          <Space size={8}>
            {L('Extract Data with AI')}
            <PaidOnlyTag />
          </Space>
        </Button>
      </div>
    </div>
  );

  return (
  <Drawer
    title={uploadedFile ? L('Upload parts') : L('New Inventory')}
    width={uploadedFile ? (isMobile ? '100vw' : '90vw') : 700}
    open={open}
    onClose={onClose}
    styles={uploadedFile ? { body: { padding: 0, overflow: 'hidden' } } : undefined}
    footer={
      <Space style={{ float: 'right' }}>
        <Button onClick={onClose}>{L('Cancel')}</Button>
        <Button
          type="primary"
          onClick={onSubmit}
          loading={submitting}
          disabled={parsedItems.length === 0 || submitting || !bulkSupplierId || !parsedInvoiceNumber.trim()}
        >
          {L('Save')} ({parsedItems.length} {parsedItems.length === 1 ? 'item' : 'items'})
        </Button>
      </Space>
    }
  >
    {uploadedFile ? (
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: '100%' }}>
        {isMobile && invoicePanel}
        <div style={{ flex: 1, overflowY: 'auto', minWidth: 0, minHeight: 0, padding: isMobile ? 16 : 24 }}>
          {parsingIndicator}
          {sharedAndItems}
        </div>
        {!isMobile && invoicePanel}
      </div>
    ) : (
      <>
        <InvoiceFileUpload
          uploadedFile={uploadedFile}
          parsing={parsing}
          captureToken={captureToken}
          onFileSelect={onFileSelect}
          onRemove={onRemoveUploadedFile}
          onParse={onParse}
        />
        {parsingIndicator}
        {sharedAndItems}
      </>
    )}

    <Modal
      title={L('New supplier')}
      open={supplierModalOpen}
      onCancel={handleSupplierModalClose}
      footer={[
        <Button key="cancel" onClick={handleSupplierModalClose}>{L('Cancel')}</Button>,
        <Button key="save" type="primary" loading={supplierCreating} onClick={handleSupplierModalSave}>
          {L('Save')}
        </Button>,
      ]}
    >
      <Form form={supplierForm} layout="vertical">
        <Form.Item name="name" label={L('Name')} rules={[{ required: true }]}>
          <Input autoFocus />
        </Form.Item>
        <Form.Item name="contactName" label={L('Contact name')}>
          <Input />
        </Form.Item>
        <Form.Item name="email" label={L('Email')}>
          <Input />
        </Form.Item>
        <Form.Item name="phone" label={L('Phone')}>
          <Input />
        </Form.Item>
        <Form.Item name="address" label={L('Address')}>
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  </Drawer>
  );
};

export default InventoryUploadDrawerPresentation;
